import { useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { Button, QuantitySelector } from '@kamalkari/ui';
import { formatPrice } from '@kamalkari/utils';
import { useCartStore } from '@/stores/cartStore';

export function CartDrawer() {
  const { isOpen, closeCart, items, removeItem, updateQuantity, getTotalPrice } = useCartStore();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={closeCart}
          />

          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-y-0 right-0 w-full max-w-md bg-background shadow-2xl z-50 flex flex-col"
          >
            <div className="flex items-center justify-between p-4 border-b border-warm-200">
              <h2 className="text-lg font-serif text-warm-900">Shopping Cart</h2>
              <button
                onClick={closeCart}
                className="p-2 hover:bg-warm-100 rounded-sm transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <ShoppingBag className="w-16 h-16 text-warm-300 mb-4" />
                <p className="text-warm-600 mb-6">Your cart is empty</p>
                <Button onClick={closeCart} asChild>
                  <Link to="/products">Continue Shopping</Link>
                </Button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  <AnimatePresence mode="popLayout">
                    {items.map((item) => (
                      <motion.div
                        key={`${item.productId}-${item.variantId || ''}`}
                        layout
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="flex gap-4 bg-white p-3 rounded-lg border border-warm-200"
                      >
                        <div className="w-20 h-24 rounded-sm overflow-hidden bg-warm-100 flex-shrink-0">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-warm-900 text-sm truncate">
                            {item.name}
                          </h3>
                          <p className="text-xs text-warm-500 mb-2">SKU: {item.sku}</p>
                          <p className="text-warm-800 font-medium mb-2">
                            {formatPrice(item.price)}
                          </p>

                          <div className="flex items-center justify-between">
                            <QuantitySelector
                              value={item.quantity}
                              onChange={(qty) =>
                                updateQuantity(item.productId, qty, item.variantId)
                              }
                              max={10}
                              className="scale-90 origin-left"
                            />
                            <button
                              onClick={() => removeItem(item.productId, item.variantId)}
                              className="p-1.5 text-warm-400 hover:text-madder transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>

                <div className="border-t border-warm-200 p-4 space-y-4">
                  <div className="flex items-center justify-between text-lg">
                    <span className="text-warm-600">Subtotal</span>
                    <span className="font-medium text-warm-900">{formatPrice(getTotalPrice())}</span>
                  </div>
                  <p className="text-xs text-warm-500">
                    Shipping and taxes calculated at checkout
                  </p>

                  <div className="space-y-2">
                    <Button onClick={closeCart} asChild className="w-full" size="lg">
                      <Link to="/checkout">
                        Checkout
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Link>
                    </Button>
                    <Button
                      onClick={closeCart}
                      asChild
                      variant="outline"
                      className="w-full"
                    >
                      <Link to="/cart">View Cart</Link>
                    </Button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
